import React from 'react';
import {AppContext} from "../../HomePage";
import {toRelativeTimeString, toDateTimeString} from "../../utils/TimeUtil";

// Status bar and domain tiles for the System Health screen. Split out of
// SystemHealth.js so the screen file stays about alert triage and this one
// about the at-a-glance summary above it.

export const STATUS_STYLE = {
    ok:       {color: '#16a34a', bg: 'rgba(34,197,94,.08)',  icon: 'pi pi-check-circle'},
    warning:  {color: '#d97706', bg: 'rgba(245,158,11,.10)', icon: 'pi pi-exclamation-triangle'},
    critical: {color: '#dc2626', bg: 'rgba(239,68,68,.10)',  icon: 'pi pi-times-circle'},
};

// Where "open screen" of each tile leads; keys match RULES[*].domain.
export const DOMAIN_LINKS = {requests: '#/canary', scheduler: '#/scheduler_monitor',
    queue: '#/queue', proxies: '#/proxy_monitor', zyte: '#/bot_groups',
    business: '#/campaign_quality', system: '#/scheduler_monitor'};

const STATUS_RANK = {critical: 0, warning: 1, ok: 2};

// Tiles show at most this many metrics before the rest move behind "show all".
const METRIC_PREVIEW = 3;

// Which metrics lead each tile. Only domains with more metrics than fit need an
// entry -- 'requests' alone carries nine, and the two that matter (how bad is
// the worst site, how many failures are ours to fix) were buried in the middle.
const LEAD_METRICS = {
    requests: ['worst_host', 'worst_rate', 'failures_real'],
    business: ['marketplace_conn_errors', 'uncaptured_products', 'paid_users_tracked'],
    scheduler: ['jobs_total', 'jobs_failed', 'worker_heartbeat_at'],
};

// Metrics whose raw value is unreadable in a tile: a timestamp printed in full
// ("2026-08-28 16:37:27.817000+00:00") tells you far less than "3 minutes ago".
const RELATIVE_METRICS = new Set(['worker_heartbeat_at']);
const PERCENT_METRICS = new Set(['worst_rate']);

const metricValue = (key, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (RELATIVE_METRICS.has(key)) return toRelativeTimeString(value);
    if (PERCENT_METRICS.has(key)) return `${value}%`;
    return String(value);
};

const metricTitle = (key, value) =>
    RELATIVE_METRICS.has(key) ? toDateTimeString(value) : undefined;

// Lead metrics first, then whatever else the domain reported, so a new backend
// metric still shows up without touching LEAD_METRICS.
const orderedMetrics = (domain, metrics) => {
    const keys = Object.keys(metrics || {});
    const lead = (LEAD_METRICS[domain] || []).filter(key => keys.includes(key));
    return [...lead, ...keys.filter(key => !lead.includes(key))];
};


const Badges = ({alerts, t}) => {
    const counts = alerts || {};
    return (
        <div className="sh-tile-badges">
            {counts.critical > 0 &&
                <span className="sh-badge sh-badge-critical">
                    {t('system_health.badge_critical', {count: counts.critical})}</span>}
            {counts.warning > 0 &&
                <span className="sh-badge sh-badge-warning">
                    {t('system_health.badge_warning', {count: counts.warning})}</span>}
            {counts.muted > 0 &&
                <span className="sh-badge sh-badge-muted">
                    {t('system_health.badge_muted', {count: counts.muted})}</span>}
        </div>);
};


const Tile = ({domain, tile, selected, onSelect}) => {
    const {t} = React.useContext(AppContext);
    const [expanded, setExpanded] = React.useState(false);
    const style = STATUS_STYLE[tile.status] || STATUS_STYLE.ok;
    const keys = orderedMetrics(domain, tile.metrics);
    const shown = expanded ? keys : keys.slice(0, METRIC_PREVIEW);
    const hidden = keys.length - shown.length;

    return (
        <div className={`sh-tile${selected ? ' sh-tile-selected' : ''}`}
             role="button" tabIndex={0} onClick={onSelect}
             onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
             aria-pressed={selected}
             style={{'--sh-accent': style.color, '--sh-accent-bg': style.bg}}>
            <div className="sh-tile-head">
                <span className="sh-tile-name">{t(`system_health.domain.${domain}`)}</span>
                <i className={`${style.icon} sh-tile-icon`} />
            </div>
            <Badges alerts={tile.alerts} t={t} />
            <div className="sh-metrics">
                {shown.map(key =>
                    <div className="sh-metric" key={key}>
                        <span className="sh-metric-label"
                              title={t(`system_health.metric.${key}`, {defaultValue: key})}>
                            {t(`system_health.metric.${key}`, {defaultValue: key})}</span>
                        <span className="sh-metric-value" title={metricTitle(key, tile.metrics[key])}>
                            {metricValue(key, tile.metrics[key])}</span>
                    </div>)}
            </div>
            <div className="sh-tile-foot">
                <a className="sh-tile-link" href={DOMAIN_LINKS[domain] || '#/management'}
                   onClick={(e) => e.stopPropagation()}>{t('system_health.open_screen')}</a>
                {(hidden > 0 || expanded) &&
                    <button type="button" className="sh-tile-more"
                            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
                        {expanded ? t('system_health.metrics_less')
                                  : t('system_health.metrics_more', {count: hidden})}</button>}
            </div>
        </div>);
};


// Worst status wins, then degraded areas float above healthy ones -- a screen
// read top-left first should not open on a green tile while a red one waits at
// the end of the row.
const sortedTiles = (tiles) => Object.entries(tiles || {}).sort(
    (a, b) => (STATUS_RANK[a[1].status] ?? 3) - (STATUS_RANK[b[1].status] ?? 3)
        || a[0].localeCompare(b[0]));


export const SystemHealthStatusBar = ({tiles, counts, lastRunAt}) => {
    const {t} = React.useContext(AppContext);
    const entries = Object.values(tiles || {});
    const degraded = entries.filter(tile => tile.status !== 'ok').length;
    const status = counts.critical > 0 ? 'critical' : (counts.warning > 0 ? 'warning' : 'ok');
    const style = STATUS_STYLE[status];

    return (
        <div className="sh-status" style={{'--sh-accent': style.color, '--sh-accent-bg': style.bg}}>
            <span className="sh-status-headline">
                <i className={style.icon} />
                {t(`system_health.headline.${status}`, {count: counts.critical || counts.warning})}
            </span>
            {status !== 'ok' &&
                <span className="sh-status-counts">
                    {counts.critical > 0 &&
                        <span className="sh-status-count">
                            <b>{counts.critical}</b> {t('system_health.severity.critical')}</span>}
                    {counts.warning > 0 &&
                        <span className="sh-status-count">
                            <b>{counts.warning}</b> {t('system_health.severity.warning')}</span>}
                    {counts.muted > 0 &&
                        <span className="sh-status-count">
                            <b>{counts.muted}</b> {t('system_health.muted_label')}</span>}
                    <span className="sh-status-count">
                        {t('system_health.degraded_areas',
                            {degraded: degraded, total: entries.length})}</span>
                </span>}
            <span className="sh-status-meta" title={toDateTimeString(lastRunAt)}>
                {t('system_health.last_run')}: {lastRunAt ? toRelativeTimeString(lastRunAt) : '—'}
            </span>
        </div>);
};


const SystemHealthTiles = ({tiles, selected, onSelect}) => (
    <div className="sh-tiles">
        {sortedTiles(tiles).map(([domain, tile]) =>
            <Tile key={domain} domain={domain} tile={tile}
                  selected={selected === domain}
                  onSelect={() => onSelect(selected === domain ? null : domain)} />)}
    </div>);

export default SystemHealthTiles;
